/**
 * GV Bridge への接続（SSE）。
 *
 * ブリッジが上流を継続的にポーリングし、新着を即座に押し出してくれるので、
 * クライアント側のポーリング待ちが無くなる。
 * 既存経路の最悪22.5秒（docs/02 §5）のうち、受信側の待ち時間が消える。
 *
 * `EventSource` は再接続を自前で行うため、通信が途切れても勝手に復帰する。
 */

import {
  isPlausibleLiveFixTime,
  packFixes,
  unpackFixes,
  type CircleArea,
  type FixSample,
  type MarkPoint,
  type PackedTrack,
} from '@gv4d/core';
import { dropOutOfOrderFixes, isNewerFix } from './dedupe.ts';
import { indexedDbTrackCache, type StoredTrackCache, type TrackCachePersistence } from './track-cache-store.ts';

export interface BridgeSnapshot {
  terminals: Array<{ terminalId: string; latest: FixSample; trackLength: number }>;
  tracks: Array<{ terminalId: string; fixes: FixSample[] }>;
  marks: Array<{ terminalId: string; marks: MarkPoint[] }>;
  markNames: Array<{ terminalId: string; names: string[] }>;
  circles: Array<{ terminalId: string; circles: CircleArea[] }>;
  passthroughCount: number;
  passthroughTypes: Record<string, number>;
  /**
   * `?compact=1`で接続したときの航跡（gv-coreの`packFixes`の形）。新しい
   * ブリッジはこちらに入れて`tracks`を空にする。古いブリッジは`compact`を
   * 知らないので`tracks`のまま送ってくる（どちらでも読めるようにしておく）
   */
  tracksPacked?: PackedTrack[];
  /** 受信した点の通し番号の最大値（古いブリッジには無い） */
  seq?: number;
  /** ブリッジの保持データの世代（古いブリッジには無い） */
  generation?: string;
  /** true なら`tracks`/`tracksPacked`は前回の`seq`より後の点だけ */
  delta?: boolean;
}

export interface BridgeUpdate {
  fixes: FixSample[];
  marks: Array<{ terminalId: string; marks: MarkPoint[] }>;
  markNames: Array<{ terminalId: string; names: string[] }>;
  circles: Array<{ terminalId: string; circles: CircleArea[] }>;
  passthrough: Array<{ type: string }>;
  /** 取り込み後の通し番号（古いブリッジには無い） */
  seq?: number;
}

export interface BridgeTerminal {
  terminalId: string;
  latest: FixSample;
  /**
   * `latest`が「未来方向にもっともらしい」時刻の点から採用されたものか。
   * false のときは、次に届いたもっともらしい点を時刻の前後に関わらず
   * 無条件に採用し直す（`apps/bridge/src/state.ts`の同名フィールド参照）。
   */
  latestIsPlausible: boolean;
  fixes: FixSample[];
  updatedAt: number;
}

export type BridgeStatus = 'idle' | 'connecting' | 'connected' | 'error' | 'auth-error';

/**
 * 切断したときの受信データ（接続先URLごと）。次に同じURLへ接続したとき、
 * これをすぐ表示し、ブリッジには続き（`since`以降）だけを頼む。
 *
 * ★ なぜ要るか（2026-10-09、利用者から「一度切断して接続しても同じ時間が
 *   必要」と指摘）。以前は接続のたびに全機の全点を受け取り直していた。
 */
interface TrackCache {
  generation: string;
  seq: number;
  terminals: Map<string, BridgeTerminal>;
  circles: Map<string, CircleArea[]>;
  marks: Map<string, MarkPoint[]>;
}
const trackCaches = new Map<string, TrackCache>();

/**
 * 端末内（IndexedDB）に残した受信データの読み込みを待つ上限。これを過ぎたら
 * 待たずに全点で接続する（読み込みが遅い端末で接続自体を遅らせないため）
 */
const PERSIST_LOAD_TIMEOUT_MS = 1500;
/** 端末内に残したデータを使う上限。これより古ければ捨てて全点を受け取る */
const PERSIST_MAX_AGE_MS = 24 * 60 * 60 * 1000;
/** 接続中に端末内へ書き出す間隔。アプリが前触れなく終了しても、この程度の遅れで済む */
const PERSIST_SAVE_INTERVAL_MS = 30_000;

function toStored(baseUrl: string, cache: TrackCache): StoredTrackCache {
  return {
    baseUrl,
    generation: cache.generation,
    seq: cache.seq,
    savedAt: Date.now(),
    terminals: [...cache.terminals.values()].map((t) => ({
      terminalId: t.terminalId,
      latest: t.latest,
      latestIsPlausible: t.latestIsPlausible,
      packed: packFixes(t.terminalId, t.fixes),
    })),
    circles: [...cache.circles].map(([terminalId, circles]) => ({ terminalId, circles })),
    marks: [...cache.marks].map(([terminalId, marks]) => ({ terminalId, marks })),
  };
}

function fromStored(stored: StoredTrackCache): TrackCache {
  const now = Date.now();
  return {
    generation: stored.generation,
    seq: stored.seq,
    terminals: new Map(
      stored.terminals.map((t) => [
        t.terminalId,
        {
          terminalId: t.terminalId,
          latest: t.latest,
          latestIsPlausible: t.latestIsPlausible,
          fixes: unpackFixes(t.packed),
          updatedAt: now,
        },
      ]),
    ),
    circles: new Map(stored.circles.map((c) => [c.terminalId, c.circles])),
    marks: new Map(stored.marks.map((m) => [m.terminalId, m.marks])),
  };
}

/** 接続直後、最初の snapshot が届かなければ「繋がっていない」と判断するまでの猶予 */
const INITIAL_TIMEOUT_MS = 8000;
/** 接続済みの状態でエラーが起きても、これだけ待って回復しなければ表示に反映する */
const ERROR_GRACE_MS = 4000;

export class BridgeConnection {
  #baseUrl: string;
  #endpoint: string;
  #source: EventSource | null = null;
  #terminals = new Map<string, BridgeTerminal>();
  #circles = new Map<string, CircleArea[]>();
  #marks = new Map<string, MarkPoint[]>();
  #maxTrackPoints: number;
  #passthroughCount = 0;
  #updates = 0;
  #everConnected = false;
  #errorTimer: ReturnType<typeof setTimeout> | null = null;
  #checkAuth?: () => Promise<boolean>;
  #authCheckInFlight = false;
  #errorGraceMs: number;
  /** ブリッジの通し番号・世代。古いブリッジでは null のまま（差分を頼まない） */
  #seq = 0;
  #generation: string | null = null;
  /** 端末内への保存先。使えない環境では null（保存しないだけ） */
  #persistence: TrackCachePersistence | null;
  #saveTimer: ReturnType<typeof setTimeout> | null = null;
  /** start()ごとに増やす。stop()後に遅れて届いた読み込み結果を無視するため */
  #startToken = 0;
  #onVisibility: (() => void) | null = null;

  status: BridgeStatus = 'idle';
  onUpdate: (() => void) | null = null;
  onStatus: ((status: BridgeStatus, message: string) => void) | null = null;

  /**
   * `checkAuth`: 合言葉ゲート（`/api/login`）が有効な大会で、`EventSource`が
   * 401 を返され続けているのに気付けない問題への対応。
   *
   * `EventSource`はHTTPステータスをJSに渡さない仕様のため、`onerror`だけでは
   * 「401で弾かれ続けている」のか「Wi-Fiの瞬断」なのか区別できない
   * （前者は`readyState`がCLOSEDに、後者はCONNECTINGのまま自動再試行される
   * 想定だが、実測ではCaddy越しの401でもCONNECTINGのまま再試行が続き、
   * 「再接続中…」が延々と出るだけで合言葉の再入力に辿り着けない不具合が
   * あった、2026-09-12）。ここで渡された関数は、ステータスコードを読める
   * 素の`fetch`で「本当に未認証か」を確かめる役目（呼び出し側＝main.tsが
   * `ensureBridgeAuth`相当の処理を渡す）。`false`（確実に401）を返したときだけ
   * `EventSource`を止めて`auth-error`を通知する。それ以外（`true`＝認証OK・
   * 判定不能なネットワークエラー）では今まで通りの再接続表示を続ける
   * （オフライン時に誤ってゲートを出さないため）。
   */
  constructor(
    baseUrl: string,
    options: {
      maxTrackPoints?: number;
      checkAuth?: () => Promise<boolean>;
      /** テスト用。既定はERROR_GRACE_MS（4秒）で、実運用では変える必要はない */
      errorGraceMs?: number;
      /**
       * 受信データを端末内に残す先。省略時はIndexedDB（使えなければ保存しない）。
       * テストでは偽物を渡す。null で無効
       */
      persistence?: TrackCachePersistence | null;
    } = {},
  ) {
    this.#baseUrl = baseUrl.replace(/\/+$/, '');
    this.#endpoint = `${this.#baseUrl}/api/stream`;
    this.#maxTrackPoints = options.maxTrackPoints ?? 20000;
    this.#checkAuth = options.checkAuth;
    this.#errorGraceMs = options.errorGraceMs ?? ERROR_GRACE_MS;
    this.#persistence = options.persistence === undefined ? indexedDbTrackCache() : options.persistence;
  }

  get terminals(): BridgeTerminal[] {
    return [...this.#terminals.values()].sort((a, b) => a.terminalId.localeCompare(b.terminalId));
  }

  get circles(): CircleArea[] {
    return [...this.#circles.values()].flat();
  }

  get marks(): MarkPoint[] {
    return [...this.#marks.values()].flat();
  }

  get stats(): { updates: number; passthroughCount: number } {
    return { updates: this.#updates, passthroughCount: this.#passthroughCount };
  }

  start(): void {
    this.status = 'connecting';
    this.#everConnected = false;
    this.onStatus?.('connecting', `接続中… (${this.#endpoint})`);

    const token = ++this.#startToken;

    // 前回の受信データがあれば、すぐ表示して続きだけを頼む。
    // まずメモリ（同じ画面のまま切断→接続）、無ければ端末内（開き直した直後）
    const cache = trackCaches.get(this.#baseUrl);
    if (cache) {
      // 引き継いだら手放す（同じ入れ物を2つの接続で共有しないため。切断時に入れ直す）
      trackCaches.delete(this.#baseUrl);
      this.#adopt(cache);
      this.#open();
      return;
    }
    const persistence = this.#persistence;
    if (!persistence) {
      this.#open();
      return;
    }
    const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), PERSIST_LOAD_TIMEOUT_MS));
    void Promise.race([persistence.load(this.#baseUrl).catch(() => null), timeout]).then((stored) => {
      if (token !== this.#startToken) return; // 待っている間に切断・再接続された
      if (stored && stored.generation && Date.now() - stored.savedAt < PERSIST_MAX_AGE_MS) {
        try {
          this.#adopt(fromStored(stored));
        } catch {
          // 壊れた保存データは使わない（全点を受け取る）
        }
      }
      this.#open();
    });
  }

  /** 前回の受信データを引き継いで、すぐ表示する */
  #adopt(cache: TrackCache): void {
    this.#terminals = cache.terminals;
    this.#circles = cache.circles;
    this.#marks = cache.marks;
    this.#seq = cache.seq;
    this.#generation = cache.generation;
    this.onUpdate?.();
  }

  /** ブリッジへ接続する。手元に前回の続きがあれば、その先だけを頼む */
  #open(): void {
    const params = new URLSearchParams({ compact: '1' });
    if (this.#generation != null) {
      params.set('since', String(this.#seq));
      params.set('gen', this.#generation);
    }

    // アプリが裏へ回ったら端末内へ書き出す（スマホではそのまま終了されることが多い）
    if (this.#persistence && typeof document !== 'undefined' && !this.#onVisibility) {
      this.#onVisibility = () => {
        if (document.visibilityState === 'hidden') this.#persistNow();
      };
      document.addEventListener('visibilitychange', this.#onVisibility);
    }

    const source = new EventSource(`${this.#endpoint}?${params.toString()}`);
    this.#source = source;

    // 猶予内に一度も繋がらなければ、URL を添えてはっきり知らせる。
    // 「切れました」だけでは何が悪いのか画面から分からず、都度ヒアリングが必要になっていた。
    const initialTimer = setTimeout(() => {
      if (!this.#everConnected) {
        this.status = 'error';
        this.onStatus?.(
          'error',
          `接続できません: ${this.#endpoint} に届いていません。URLとファイアウォールを確認してください`,
        );
      }
    }, INITIAL_TIMEOUT_MS);

    const markHealthy = () => {
      this.#everConnected = true;
      clearTimeout(initialTimer);
      if (this.#errorTimer) {
        clearTimeout(this.#errorTimer);
        this.#errorTimer = null;
      }
    };

    source.addEventListener('snapshot', (e) => {
      markHealthy();
      const snap = JSON.parse((e as MessageEvent).data) as BridgeSnapshot;
      const tracks = snap.tracksPacked
        ? snap.tracksPacked.map((p) => ({ terminalId: p.terminalId, fixes: unpackFixes(p) }))
        : snap.tracks;
      const isDelta = snap.delta === true && snap.generation != null && snap.generation === this.#generation;
      this.#seq = snap.seq ?? 0;
      this.#generation = snap.generation ?? null;
      if (isDelta) {
        this.#mergeDelta(snap, tracks);
      } else {
        this.#replaceAll(snap, tracks);
      }
      this.#circles.clear();
      for (const c of snap.circles) this.#circles.set(c.terminalId, c.circles);
      this.#marks.clear();
      for (const m of snap.marks) this.#marks.set(m.terminalId, m.marks);
      this.#passthroughCount = snap.passthroughCount;

      this.status = 'connected';
      this.onStatus?.('connected', '接続しました');
      this.onUpdate?.();
      this.#scheduleSave();
    });

    source.addEventListener('update', (e) => {
      markHealthy();
      const update = JSON.parse((e as MessageEvent).data) as BridgeUpdate;
      this.#updates++;
      if (typeof update.seq === 'number') this.#seq = update.seq;
      this.#applyFixes(update.fixes);
      for (const c of update.circles) this.#circles.set(c.terminalId, c.circles);
      for (const m of update.marks) this.#marks.set(m.terminalId, m.marks);
      this.#passthroughCount += update.passthrough.length;

      this.onUpdate?.();
      this.#scheduleSave();
    });

    source.onerror = () => {
      // EventSource は readyState=CONNECTING で自動的に再接続を試みる。
      // これは Wi-Fi の瞬断などでもごく普通に起きるので、error イベント1回ごとに
      // 「切れました」と出すと、実際は数秒で繋がり直っているのに誤報になる。
      // ここでは猶予時間だけ待ち、その間に markHealthy() が呼ばれなければ本当に切れたとみなす。
      if (this.#errorTimer || !this.#everConnected) return; // 初回接続待ちは initialTimer 側が担当
      this.#errorTimer = setTimeout(() => {
        void this.#handlePersistentError(source);
      }, this.#errorGraceMs);
    };
  }

  /** 全点のスナップショットで置き換える（初回・世代が変わった・古いブリッジ） */
  #replaceAll(snap: BridgeSnapshot, tracks: Array<{ terminalId: string; fixes: FixSample[] }>): void {
    this.#terminals.clear();
    for (const track of tracks) {
      // isNewerFix内でisPlausibleLiveFixTimeも判定されるため、ここで
      // 得られるfixesは必ず未来方向にもっともらしい点だけになる
      const fixes = dropOutOfOrderFixes(track.fixes);
      const rawLatest = snap.terminals.find((t) => t.terminalId === track.terminalId)?.latest;
      // ★ サーバーの`latest`は、新規端末の最初の1点が未来時刻だった場合など
      //   `latestIsPlausible=false`のままでもスナップショットに乗ることがある
      //   （コードレビュー指摘、2026-09-13）。ここで改めて検査し、ダメなら
      //   検証済みのfixesの末尾へフォールバックする
      const latestIsPlausible = rawLatest != null && isPlausibleLiveFixTime(rawLatest.t);
      const latest = latestIsPlausible ? rawLatest! : fixes[fixes.length - 1];
      if (!latest) continue; // 有効な現在地が無ければ、この端末はまだ表示しない
      this.#terminals.set(track.terminalId, {
        terminalId: track.terminalId,
        latest,
        // fixesへのフォールバックも含め、ここで採用したlatestは必ずもっともらしい
        latestIsPlausible: true,
        fixes,
        updatedAt: Date.now(),
      });
    }
  }

  /**
   * 前回の続き（`since`より後の点だけ）を手元の航跡に足す。重複・巻き戻りは
   * `isNewerFix`で落ちる（EventSourceの自動再接続は最初のURLのまま、つまり
   * 古い`since`で繋ぎ直すので、既に持っている点が届くことがある）
   */
  #mergeDelta(snap: BridgeSnapshot, tracks: Array<{ terminalId: string; fixes: FixSample[] }>): void {
    for (const track of tracks) this.#applyFixes(track.fixes);
    // 現在位置は端末一覧の`latest`にも合わせる（差分に点が無い端末もある）
    for (const info of snap.terminals) {
      const t = this.#terminals.get(info.terminalId);
      if (!t || !isPlausibleLiveFixTime(info.latest.t)) continue;
      if (!t.latestIsPlausible || info.latest.t >= t.latest.t) {
        t.latest = info.latest;
        t.latestIsPlausible = true;
      }
    }
  }

  /** 新着の点を端末ごとの航跡に足す（update・差分スナップショット共通） */
  #applyFixes(fixes: FixSample[]): void {
    for (const fix of fixes) {
      // 実際より先の時刻を持つ点は、そもそも端末の状態に触れさせない
      // （dedupe.ts参照。latestの基準として採用してしまうと、以後の
      // 正しい時刻の点が全部「基準より古い」と判定され捨てられ続ける）
      if (!isPlausibleLiveFixTime(fix.t)) continue;

      let t = this.#terminals.get(fix.terminalId);
      if (!t) {
        // ここに来る時点でfixは既にisPlausibleLiveFixTimeを通過済み
        t = { terminalId: fix.terminalId, latest: fix, latestIsPlausible: true, fixes: [], updatedAt: Date.now() };
        this.#terminals.set(fix.terminalId, t);
      }
      // 再送された古い点が紛れ込むと航跡が一瞬過去へ巻き戻って見えるため、
      // 直前に採用した点より新しい時刻のものだけ追加する（dedupe.ts参照）
      if (isNewerFix(t.fixes[t.fixes.length - 1], fix)) {
        t.fixes.push(fix);
        if (t.fixes.length > this.#maxTrackPoints) {
          t.fixes.splice(0, t.fixes.length - this.#maxTrackPoints);
        }
      }
      if (!t.latestIsPlausible || fix.t >= t.latest.t) {
        t.latest = fix;
        t.latestIsPlausible = true;
      }
      t.updatedAt = Date.now();
    }
  }

  /**
   * 猶予時間を過ぎても回復しない`error`を実際に処理する。
   *
   * ★ `stop()`や新しい`start()`で`this.#source`が入れ替わった後に、古い
   *   `EventSource`のタイマーが遅れて発火することがある。`source`（クロージャで
   *   捕まえた「このタイマーの持ち主」）と`this.#source`（今アクティブなもの）が
   *   一致するときだけ状態を変える。
   */
  async #handlePersistentError(source: EventSource): Promise<void> {
    this.#errorTimer = null;
    if (this.#source !== source) return;

    if (this.#checkAuth && !this.#authCheckInFlight) {
      this.#authCheckInFlight = true;
      let authOk: boolean;
      try {
        authOk = await this.#checkAuth();
      } finally {
        this.#authCheckInFlight = false;
      }
      if (this.#source !== source) return; // 待っている間に切断・再接続されていた
      if (!authOk) {
        source.close();
        this.status = 'auth-error';
        this.onStatus?.('auth-error', '合言葉の確認が必要です');
        return;
      }
    }

    const state = source.readyState;
    if (state === EventSource.CLOSED) {
      this.status = 'error';
      this.onStatus?.(
        'error',
        `接続が切れました（${this.#endpoint}）。ページを再読み込みするか再接続してください`,
      );
    } else {
      // まだ再接続を試みている最中。致命的ではないので状態だけ軽く伝える
      this.onStatus?.('connecting', `再接続中… (${this.#endpoint})`);
    }
  }

  /** 端末内への書き出しを、一定間隔に1回にまとめて予約する */
  #scheduleSave(): void {
    if (!this.#persistence || this.#generation == null || this.#saveTimer) return;
    this.#saveTimer = setTimeout(() => {
      this.#saveTimer = null;
      this.#persistNow();
    }, PERSIST_SAVE_INTERVAL_MS);
  }

  /** 今の受信データ（無ければ null） */
  #currentCache(): TrackCache | null {
    if (this.#generation == null || this.#terminals.size === 0) return null;
    return {
      generation: this.#generation,
      seq: this.#seq,
      terminals: this.#terminals,
      circles: this.#circles,
      marks: this.#marks,
    };
  }

  /** 端末内へ書き出す。失敗しても接続には影響させない */
  #persistNow(): void {
    const cache = this.#currentCache();
    if (!this.#persistence || !cache) return;
    let stored: StoredTrackCache;
    try {
      stored = toStored(this.#baseUrl, cache);
    } catch {
      return;
    }
    void this.#persistence.save(stored).catch(() => {});
  }

  stop(): void {
    this.#startToken++; // 読み込み待ちのstart()を無効にする
    // 次に同じURLへ接続したとき、続きだけを受け取れるように残しておく
    const cache = this.#currentCache();
    if (cache) trackCaches.set(this.#baseUrl, cache);
    if (this.#saveTimer) clearTimeout(this.#saveTimer);
    this.#saveTimer = null;
    this.#persistNow();
    if (this.#onVisibility) document.removeEventListener('visibilitychange', this.#onVisibility);
    this.#onVisibility = null;
    if (this.#errorTimer) clearTimeout(this.#errorTimer);
    this.#errorTimer = null;
    this.#source?.close();
    this.#source = null;
    this.status = 'idle';
    this.onStatus?.('idle', '切断しました');
  }
}
